import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Phone, MapPin, FileText, Edit2, Save } from 'lucide-react';
import { HistorialActividad } from './HistorialActividad';
import { BotonesLlamar } from './Softphone';

export const ContactDetailModal = ({ contact, type, isOpen, onClose, onSave, orgId }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editData, setEditData] = React.useState(contact || {});

  React.useEffect(() => {
    setEditData(contact || {});
  }, [contact]);

  if (!isOpen || !contact) return null;

  const handleSave = async () => {
    await onSave(editData);
    setIsEditing(false);
  };

  const fields = type === 'seller' ? [
    { key: 'name', label: 'Nombre', icon: <FileText size={16} /> },
    { key: 'email', label: 'Email', icon: <Mail size={16} /> },
    { key: 'phone', label: 'Teléfono', icon: <Phone size={16} /> },
    { key: 'property_address', label: 'Dirección Propiedad', icon: <MapPin size={16} /> },
    { key: 'property_price', label: 'Precio', icon: <FileText size={16} />, type: 'number' },
    { key: 'property_type', label: 'Tipo Propiedad', icon: <FileText size={16} /> },
    { key: 'status', label: 'Estado', icon: <FileText size={16} />, select: ['Prospecto', 'Contactado', 'Negociando', 'Vendido', 'Archivado'] },
    { key: 'notes', label: 'Notas', icon: <FileText size={16} />, multiline: true },
  ] : [
    { key: 'name', label: 'Nombre', icon: <FileText size={16} /> },
    { key: 'email', label: 'Email', icon: <Mail size={16} /> },
    { key: 'phone', label: 'Teléfono', icon: <Phone size={16} /> },
    { key: 'budget_min', label: 'Presupuesto Mín', icon: <FileText size={16} />, type: 'number' },
    { key: 'budget_max', label: 'Presupuesto Máx', icon: <FileText size={16} />, type: 'number' },
    { key: 'location_preference', label: 'Zona Preferida', icon: <MapPin size={16} /> },
    { key: 'property_type', label: 'Tipo Buscado', icon: <FileText size={16} /> },
    { key: 'status', label: 'Estado', icon: <FileText size={16} />, select: ['Buscando', 'Visitando', 'Negociando', 'Comprado', 'Archivado'] },
    { key: 'notes', label: 'Notas', icon: <FileText size={16} />, multiline: true },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="bg-gray-900 rounded-2xl border border-white/10 p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold text-white bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {contact.name || contact.email}
              </h2>
              <p className="text-white/60 text-sm mt-1">
                {type === 'seller' ? 'Vendedor' : 'Comprador'} · ID: {contact.seller_id || contact.buyer_id}
              </p>
              {/* Llamar como Marcos o como Sergio: queda apuntado quien llamo. */}
              {(contact.telefono || contact.phone) && (
                <div className="mt-2">
                  <BotonesLlamar
                    telefono={contact.telefono || contact.phone}
                    nombre={contact.nombre || contact.name}
                    sellerId={type === 'seller' ? contact.seller_id : null}
                    buyerId={type === 'buyer' ? contact.buyer_id : null}
                    contactId={contact.contact_id || null}
                  />
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition"
                >
                  <Edit2 size={20} />
                </button>
              ) : (
                <button
                  onClick={handleSave}
                  className="p-3 rounded-lg bg-green-600 hover:bg-green-700 text-white transition"
                >
                  <Save size={20} />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-3 rounded-lg bg-gray-800 hover:bg-gray-700 text-white transition"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fields.map((field) => (
              <div
                key={field.key}
                className={`${field.multiline ? 'md:col-span-2' : ''} bg-white/5 rounded-xl p-4 border border-white/10`}
              >
                <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
                  {field.icon}
                  <span className="font-semibold">{field.label}</span>
                </div>
                {isEditing ? (
                  field.select ? (
                    <select
                      value={editData[field.key] || ''}
                      onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                      className="w-full bg-gray-800 text-white p-3 rounded-lg border border-white/20 focus:border-purple-500 focus:outline-none"
                    >
                      {field.select.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : field.multiline ? (
                    <textarea
                      value={editData[field.key] || ''}
                      onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                      className="w-full bg-gray-800 text-white p-3 rounded-lg border border-white/20 focus:border-purple-500 focus:outline-none min-h-[100px]"
                      placeholder={`Agregar ${field.label.toLowerCase()}...`}
                    />
                  ) : (
                    <input
                      type={field.type || 'text'}
                      value={editData[field.key] || ''}
                      onChange={(e) => setEditData({ ...editData, [field.key]: e.target.value })}
                      className="w-full bg-gray-800 text-white p-3 rounded-lg border border-white/20 focus:border-purple-500 focus:outline-none"
                      placeholder={`Agregar ${field.label.toLowerCase()}...`}
                    />
                  )
                ) : (
                  <p className="text-white text-lg">
                    {field.type === 'number' && editData[field.key] 
                      ? `€${Number(editData[field.key]).toLocaleString()}`
                      : editData[field.key] || (
                        <span className="text-white/30 italic">No especificado</span>
                      )}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Documents section */}
          <div className="mt-6 p-6 bg-white/5 rounded-xl border border-white/10">
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <FileText size={20} />
              Documentos
            </h3>
            {contact.documents && contact.documents.length > 0 ? (
              <div className="space-y-2">
                {contact.documents.map((doc, i) => (
                  <a
                    key={i}
                    href={doc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block p-3 bg-gray-800 rounded-lg hover:bg-gray-700 text-blue-400 transition"
                  >
                    📄 Documento {i + 1}
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-white/40 italic">Sin documentos adjuntos</p>
            )}
          </div>

          {/* Todo lo que ha pasado con esta persona: llamadas, correos,
              WhatsApp, Idealista y las notas que se dejen a mano. */}
          <HistorialActividad
            orgId={orgId}
            sellerId={type === 'seller' ? contact.seller_id : null}
            buyerId={type === 'buyer' ? contact.buyer_id : null}
            contactId={contact.contact_id || null}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
